import React from 'react';
import {
  Button,
  Text,
  Title2,
  Body1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ErrorCircleRegular,
  ArrowClockwiseRegular,
} from '@fluentui/react-icons';

/**
 * Props for the ErrorBoundary component.
 */
interface ErrorBoundaryProps {
  /**
   * Child components to render within the error boundary.
   */
  children: React.ReactNode;

  /**
   * Optional fallback UI to render when an error occurs.
   * If not provided, a default error UI will be shown.
   */
  fallback?: React.ComponentType<ErrorFallbackProps>;

  /**
   * Optional callback function that will be called when an error is caught.
   * Useful for logging errors to an external service.
   *
   * @param error - The error that was caught
   * @param errorInfo - Additional information about the error
   */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

/**
 * Props passed to custom fallback components.
 */
export interface ErrorFallbackProps {
  /**
   * The error that was caught.
   */
  error: Error;

  /**
   * Function to reset the error boundary and attempt to re-render the component tree.
   */
  resetErrorBoundary: () => void;
}

/**
 * State for the ErrorBoundary component.
 */
interface ErrorBoundaryState {
  /**
   * Whether an error has been caught.
   */
  hasError: boolean;

  /**
   * The error that was caught, if any.
   */
  error: Error | null;
}

/**
 * Styles for the default error fallback UI.
 */
const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: tokens.spacingVerticalXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    textAlign: 'center',
  },
  icon: {
    fontSize: '64px',
    color: tokens.colorPaletteRedForeground1,
    marginBottom: tokens.spacingVerticalL,
  },
  title: {
    marginBottom: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground1,
  },
  message: {
    marginBottom: tokens.spacingVerticalL,
    maxWidth: '600px',
    color: tokens.colorNeutralForeground2,
  },
  errorDetails: {
    marginTop: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    maxWidth: '800px',
    width: '100%',
    textAlign: 'left',
  },
  errorMessage: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    wordBreak: 'break-word',
    marginBottom: tokens.spacingVerticalS,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalL,
  },
});

/**
 * Default error fallback component that displays a user-friendly error message.
 */
function DefaultErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps): React.ReactElement {
  const styles = useStyles();
  const [showDetails, setShowDetails] = React.useState(false);

  return (
    <div className={styles.container}>
      <ErrorCircleRegular className={styles.icon} />
      <Title2 className={styles.title}>Oops! Something went wrong</Title2>
      <Body1 className={styles.message}>
        We're sorry, but an unexpected error occurred. This could be a temporary issue.
        Please try reloading the page. If the problem persists, contact your administrator.
      </Body1>

      <div className={styles.actions}>
        <Button
          appearance="primary"
          icon={<ArrowClockwiseRegular />}
          onClick={resetErrorBoundary}
        >
          Try Again
        </Button>
        <Button
          appearance="secondary"
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? 'Hide Details' : 'Show Details'}
        </Button>
      </div>

      {showDetails && (
        <div className={styles.errorDetails}>
          <Text weight="semibold" block>Error Details:</Text>
          <div className={styles.errorMessage}>
            {error.message}
          </div>
          {error.stack && (
            <>
              <Text weight="semibold" block style={{ marginTop: tokens.spacingVerticalS }}>
                Stack Trace:
              </Text>
              <pre style={{
                fontSize: tokens.fontSizeBase200,
                overflow: 'auto',
                margin: 0,
                padding: tokens.spacingVerticalS,
              }}>
                {error.stack}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * ErrorBoundary component that catches JavaScript errors anywhere in the child
 * component tree and displays a fallback UI instead of crashing the entire application.
 *
 * This is a class component because React error boundaries must be implemented as
 * class components (error boundaries are not yet supported with function components).
 *
 * **Why use React.memo:**
 * This component is wrapped with React.memo to prevent unnecessary re-renders when
 * parent components update, as error boundaries don't need to re-render unless their
 * props change. This is especially important since the ErrorBoundary wraps the entire
 * application tree.
 *
 * @example
 * ```tsx
 * // Wrap your app with the ErrorBoundary
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * // With custom error handler
 * <ErrorBoundary onError={(error, errorInfo) => {
 *   console.error('Error caught:', error, errorInfo);
 *   // Send to error reporting service
 * }}>
 *   <App />
 * </ErrorBoundary>
 *
 * // With custom fallback
 * <ErrorBoundary fallback={CustomErrorFallback}>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  /**
   * Static method called when an error is thrown in a descendant component.
   * Updates state so the next render will show the fallback UI.
   *
   * @param error - The error that was thrown
   * @returns The new state object
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Lifecycle method called after an error has been thrown by a descendant component.
   * Used for logging error information.
   *
   * @param error - The error that was thrown
   * @param errorInfo - Information about which component threw the error
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * Resets the error boundary state, allowing the component tree to be re-rendered.
   * This is useful for giving users a chance to retry after an error.
   */
  private resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return (
        <FallbackComponent
          error={this.state.error}
          resetErrorBoundary={this.resetErrorBoundary}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
